import { useState, useEffect, useCallback, useRef } from 'react';

interface ReloadDetectionOptions {
  storageKey?: string;
  warningMessage?: string;
  onReloadDetected?: (data: unknown) => void;
  autoCleanup?: boolean;
  enableManualTrigger?: boolean;
}

interface ReloadData {
  timestamp: number;
  hasUnsavedChanges: boolean;
  url: string;
  customData?: unknown;
}

const useReloadDetection = (options: ReloadDetectionOptions = {}) => {
  const {
    storageKey = 'reload_detection',
    warningMessage = 'Bạn có chắc chắn muốn rời khỏi trang? Những thay đổi chưa được lưu sẽ bị mất.',
    autoCleanup = true,
    enableManualTrigger = false,
  } = options;

  const [wasReloaded, setWasReloaded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savedData, setSavedData] = useState<ReloadData | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [lastChangeTimestamp, setLastChangeTimestamp] = useState<number | null>(null);
  const isUnloadingRef = useRef(false);

  // Kiểm tra reload khi component mount
  useEffect(() => {
    const checkReload = () => {
      const reloadData = localStorage.getItem(storageKey);
      if (reloadData) {
        try {
          const parsedData = JSON.parse(reloadData) as ReloadData;
          setWasReloaded(true);
          setSavedData(parsedData);
          if (autoCleanup) {
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          console.error('Error parsing reload data:', error);
          localStorage.removeItem(storageKey);
        }
      }
    };

    checkReload();
  }, [storageKey, autoCleanup]);

  // Xử lý beforeunload
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isUnloadingRef.current) {
        e.preventDefault();
        e.returnValue = warningMessage;
        return warningMessage;
      }
    },
    [hasUnsavedChanges, warningMessage]
  );

  // Xử lý unload
  const handleUnload = useCallback(() => {
    isUnloadingRef.current = true;
    if (hasUnsavedChanges) {
      const dataToSave: ReloadData = {
        timestamp: Date.now(),
        hasUnsavedChanges: true,
        url: window.location.href,
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    }
  }, [hasUnsavedChanges, storageKey]);

  // Đăng ký event listeners
  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [handleBeforeUnload, handleUnload]);

  // Các phương thức tiện ích
  const markAsUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
    setLastChangeTimestamp(Date.now());
  }, []);

  const markAsSaved = useCallback(() => {
    setHasUnsavedChanges(false);
    setLastChangeTimestamp(null);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const dismissReloadNotification = useCallback(() => {
    setWasReloaded(false);
    setSavedData(null);
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const saveDataForReload = useCallback(
    (data: unknown) => {
      const dataToSave: ReloadData = {
        timestamp: Date.now(),
        hasUnsavedChanges: true,
        url: window.location.href,
        customData: data,
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    },
    [storageKey]
  );

  // Manual trigger methods
  const showConfirmationDialog = useCallback(() => {
    if (hasUnsavedChanges && enableManualTrigger) {
      setShowDialog(true);
    }
  }, [hasUnsavedChanges, enableManualTrigger]);

  const hideConfirmationDialog = useCallback(() => {
    setShowDialog(false);
  }, []);

  return {
    wasReloaded,
    hasUnsavedChanges,
    savedData,
    showDialog,
    lastChangeTimestamp,
    markAsUnsaved,
    markAsSaved,
    dismissReloadNotification,
    saveDataForReload,
    showConfirmationDialog,
    hideConfirmationDialog,
  };
};

export default useReloadDetection;
